import { useCallback, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSolum } from '../data/client'

/**
 * The saved view a person chose for ONE RELATIONSHIP (#2375, ADR F-84 §4).
 *
 * Measured on e31d7dc3: a nested grid's view choice was React state only
 * (`RelationBand` `viewId`, `RelationCascade` `viewBySlot`/`ownView`), so it
 * was lost on every navigation. The ruling: a nested relationship's saved view
 * is SHARED across parent records — choosing "Active price lists" under one
 * product is the choice for that relationship under every product.
 *
 * So the durable key is the HOST, the PARENT ENTITY TYPE and the relationship
 * SLOT (`<relType>:<direction>:<farEntity>`, `relations.ts`) — and never the
 * parent record's UUID. The parent may scope only transient state (expansion,
 * selection, an in-progress search), which stays in React.
 *
 * Stored where every other per-person grid preference lives —
 * `core.user_setting` (own rows only, `0035`) through the existing
 * `set_user_setting` door — so no new store, door or grant exists for it. The
 * view DEFINITIONS remain the far entity's `core.view` rows, under their own
 * system / solution / tenant / persona / user custody.
 */
export const NESTED_VIEWS_KEY = 'relationships.views'

export type NestedViewHost = 'console'

/** The durable key. Deliberately takes no record id — see above. */
export function nestedViewKey(host: NestedViewHost, parentEntity: string, slotId: string): string {
  return `${host}|${parentEntity}|${slotId}`
}

type ChoiceMap = Record<string, string>

const sanitize = (value: unknown): ChoiceMap => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
  return Object.fromEntries(Object.entries(value as Record<string, unknown>)
    .filter((entry): entry is [string, string] => typeof entry[1] === 'string' && entry[1].length > 0))
}

export function useNestedViewChoice(
  parentEntity: string, slotId: string | null | undefined, host: NestedViewHost = 'console',
): [string, (viewId: string) => void] {
  const { client, isLive } = useSolum()
  const qc = useQueryClient()
  const settings = useQuery({
    queryKey: ['user-settings'],
    queryFn: () => client.raw().rpcScalar<Record<string, unknown>>('my_settings'),
    enabled: isLive,
    staleTime: 60_000,
  })
  const key = slotId ? nestedViewKey(host, parentEntity, slotId) : null
  const stored = key ? sanitize(settings.data?.[NESTED_VIEWS_KEY])[key] ?? '' : ''
  /* The choice made in THIS session wins immediately; the write follows. */
  const [local, setLocal] = useState<Record<string, string>>({})
  const save = useMutation({
    mutationFn: (next: ChoiceMap) => client.raw().rpcScalar('set_user_setting', {
      p_key: NESTED_VIEWS_KEY, p_value: next,
    }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['user-settings'] }) },
  })
  const choose = useCallback((viewId: string) => {
    if (!key) return
    setLocal((current) => ({ ...current, [key]: viewId }))
    if (!isLive) return
    save.mutate({ ...sanitize(settings.data?.[NESTED_VIEWS_KEY]), [key]: viewId })
  }, [key, isLive, save, settings.data])
  return [key ? local[key] ?? stored : '', choose]
}
